import React, { useEffect, useMemo, useRef, useState, } from "react";
import { Portal as ReactPortal } from "react-portal";
import { useOnClickOutside } from "../../hooks";
import { FocusTrap, FocusTrapFeatures } from "@headlessui/react";
import Button from "../Button";
import { useFocusWithin } from "react-aria";
import { focusFirstFocusableElement, } from "../../util/findFirstFocusableElement";
function Portal(props) {
    const { render, targetRef, onClickOutside, considerScreenBoundaries } = props;
    const targetRect = useRect(targetRef);
    const portalRef = useRef(null);
    const portalRect = useRect(portalRef);
    const anchor = useMemo(() => props.targetAnchor.toLowerCase(), [props.targetAnchor]);
    const portalAnchor = useMemo(() => {
        let portalAnchor = props.anchor;
        if (portalAnchor == null) {
            portalAnchor = anchor.startsWith("top")
                ? anchor.replace("top", "bottom")
                : anchor.startsWith("bottom")
                    ? anchor.replace("bottom", "top")
                    : anchor;
        }
        return portalAnchor.toLowerCase();
    }, [props.anchor]);
    useOnClickOutside(targetRef, onClickOutside, props.subPortalRefs ? [...props.subPortalRefs, portalRef] : [portalRef]);
    const isAttachedToDOM = targetRect != null && portalRect != null;
    const calculateTopOffset = () => {
        if (!isAttachedToDOM)
            return undefined;
        const targetOffset = window.scrollY +
            (props.offsetY ?? 0) +
            targetRect.top +
            (anchor.startsWith("bottom")
                ? targetRect.height
                : anchor.startsWith("top")
                    ? 0
                    : targetRect.height / 2);
        const portalOffset = portalAnchor.startsWith("top")
            ? 0
            : portalAnchor.startsWith("bottom")
                ? -portalRect.height
                : -portalRect.height / 2;
        let finalOffset = targetOffset + portalOffset;
        if (considerScreenBoundaries) {
            const minTop = 0;
            finalOffset = Math.max(minTop, finalOffset);
        }
        return finalOffset;
    };
    const calculateLeftOffset = () => {
        if (!isAttachedToDOM)
            return undefined;
        const targetOffset = window.scrollX +
            (props.offsetX ?? 0) +
            targetRect.left +
            (anchor.endsWith("left")
                ? 0
                : anchor.endsWith("right")
                    ? targetRect.width
                    : targetRect.width / 2);
        const portalOffset = portalAnchor.endsWith("right")
            ? -portalRect.width
            : portalAnchor.endsWith("left")
                ? 0
                : -portalRect.width / 2;
        return targetOffset + portalOffset;
    };
    const [visibility, setVisibility] = useState("hidden");
    const style = {
        overflowY: considerScreenBoundaries ? "auto" : undefined,
        width: props.adoptTargetWidth ? targetRect?.width : undefined,
        visibility: visibility,
        maxHeight: considerScreenBoundaries
            ? anchor.startsWith("top")
                ? `${targetRect ? targetRect.top : 0}px`
                : anchor.startsWith("bottom")
                    ? `${window.innerHeight - (targetRect ? targetRect.bottom : 0)}px`
                    : undefined
            : undefined,
        top: visibility === "visible" ? calculateTopOffset() : 0,
        left: visibility === "visible" ? calculateLeftOffset() : 0,
        zIndex: 999,
        position: "absolute",
    };
    const trapRef = useRef(null);
    useEffect(() => {
        if (!isAttachedToDOM)
            return;
        setVisibility("visible");
        const portalElement = portalRef.current;
        if (props.autofocus && portalElement != null) {
            focusFirstFocusableElement(portalElement);
        }
    }, [isAttachedToDOM]);
    const closePopupRef = useRef(null);
    const isFocusWithin = useRef(false);
    useEffect(() => {
        return () => {
            if (isFocusWithin.current) {
                const parentElement = props.parentRef.current;
                if (parentElement == null)
                    return;
                focusFirstFocusableElement(parentElement);
            }
        };
    }, []);
    const { focusWithinProps } = useFocusWithin({
        onFocusWithinChange: (value) => (isFocusWithin.current = value),
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { className: "sr-only", onFocus: () => closePopupRef.current?.focus() }, "Focus trap"),
        React.createElement(ReactPortal, null,
            React.createElement(FocusTrap, { ref: trapRef, role: "dialog", ...focusWithinProps, features: FocusTrapFeatures.TabLock },
                React.createElement(Button, { ref: closePopupRef, kind: "text", onClick: onClickOutside, className: "sr-only !fixed !top-0 !left-0 !p-1  focus-visible:z-notification-layer focus-visible:not-sr-only" }, "Close Popup"),
                render({ ref: portalRef, style })))));
}
const useEvent = (event, trigger, useCapture) => {
    useEffect(() => {
        trigger();
        window.addEventListener(event, trigger, useCapture);
        return () => window.removeEventListener(event, trigger, useCapture);
    }, []);
};
export const useRect = (ref, isTrackingEnabled = true) => {
    const [rect, setRect] = useState();
    useEffect(() => {
        set();
    });
    function set() {
        if (isTrackingEnabled) {
            const newRect = ref.current?.getBoundingClientRect();
            const isRectChanged = rect?.x !== newRect?.x ||
                rect?.y !== newRect?.y ||
                rect?.width !== newRect?.width ||
                rect?.height !== newRect?.height;
            if (isRectChanged)
                return setRect(newRect);
        }
    }
    useEvent("resize", set);
    useEvent("scroll", set, true);
    return rect;
};
function PortalTarget({ isVisible, anchor, portal, renderChild, ...rest }) {
    const targetRef = useRef(null);
    const parentRef = useRef(null);
    return (React.createElement("div", { ref: parentRef, tabIndex: -1, ...rest },
        renderChild(targetRef),
        isVisible && (React.createElement(Portal, { ...portal, parentRef: parentRef, targetAnchor: anchor, targetRef: targetRef }))));
}
export default PortalTarget;
//# sourceMappingURL=PortalTarget.js.map